import React, { useState } from "react";
import { Link } from "react-router-dom";
import { requestPasswordReset, humanizeApiError } from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    
    if (!email || !email.includes("@")) {
      setErr("Geçerli bir e-posta adresi girin.");
      return;
    }
    
    setErr(null);
    setLoading(true);
    try {
      await requestPasswordReset(email);
      setSent(true);
    } catch (e: any) {
      setErr(humanizeApiError(e?.message || "api_error"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="forgot-container">
      <div className="forgot-content">
        {/* Logo */}
        <div className="logo-container">
          <img src="/logo/logo.png" alt="SavNot" className="logo-image" />
          <h1 className="app-name">SavNot</h1>
        </div>

        <h2 className="forgot-title">Şifremi Unuttum</h2>
        <p className="forgot-description">
          E-posta adresini gir; sana şifre sıfırlama bağlantısı gönderelim.
        </p>

        {sent ? (
          <div className="success-box">
            <div className="success-icon">✅</div>
            <div className="success-title">E-posta gönderildi</div>
            <div className="success-message">
              Eğer bir hesabın varsa, gelen kutunu kontrol et.<br />
              Spam/Junk klasörünü de kontrol etmeyi unutma.
            </div>
            <Link to="/login" className="back-button">
              Giriş Sayfasına Dön
            </Link>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="forgot-form">
            {/* Email Input */}
            <input
              className="forgot-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="E-posta adresin"
              autoComplete="email"
            />

            {/* Error */}
            {err && <p className="forgot-error">{err}</p>}

            {/* Submit Button */}
            <button
              type="submit"
              className="forgot-button"
              disabled={loading || !email}
            >
              {loading ? 'Gönderiliyor...' : 'Sıfırlama Bağlantısı Gönder'}
            </button>

            {/* Back to Login */}
            <Link to="/login" className="login-link">
              ← Giriş Sayfasına Dön
            </Link>
          </form>
        )}
      </div>

      <style>{`
        .forgot-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: #000;
          padding: 24px;
        }

        .forgot-content {
          width: 100%;
          max-width: 420px;
        }

        .logo-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          margin-bottom: 48px;
        }

        .logo-image {
          width: 80px;
          height: 80px;
          object-fit: contain;
        }

        .app-name {
          font-size: 24px;
          font-weight: 800;
          color: #114232;
          margin-top: 8px;
          margin-bottom: 0;
        }

        .forgot-title {
          font-size: 28px;
          font-weight: 800;
          color: #fff;
          margin-bottom: 12px;
          text-align: center;
        }

        .forgot-description {
          font-size: 14px;
          color: #999;
          text-align: center;
          margin-bottom: 32px;
          line-height: 1.5;
        }

        .forgot-form {
          display: flex;
          flex-direction: column;
        }

        .forgot-input {
          background-color: #1a1a1a;
          border: 2px solid #114232;
          border-radius: 12px;
          padding: 16px;
          margin-bottom: 16px;
          font-size: 16px;
          color: #fff;
          width: 100%;
          box-sizing: border-box;
        }

        .forgot-input::placeholder {
          color: #666;
        }

        .forgot-input:focus {
          outline: none;
          border-color: #1a6b4f;
        }

        .forgot-button {
          background-color: #114232;
          border: none;
          border-radius: 12px;
          padding: 16px;
          color: #fff;
          font-size: 16px;
          font-weight: 700;
          cursor: pointer;
          margin-top: 8px;
          width: 100%;
        }

        .forgot-button:hover:not(:disabled) {
          background-color: #1a6b4f;
        }

        .forgot-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .forgot-error {
          color: #ff6b6b;
          text-align: center;
          margin-bottom: 12px;
          font-size: 14px;
          background-color: rgba(255, 107, 107, 0.1);
          padding: 12px;
          border-radius: 8px;
          border: 1px solid rgba(255, 107, 107, 0.2);
        }

        .login-link {
          color: #114232;
          font-size: 14px;
          font-weight: 600;
          text-align: center;
          margin-top: 24px;
          text-decoration: none;
          display: block;
        }

        .login-link:hover {
          text-decoration: underline;
        }

        .success-box {
          background-color: #1a1a1a;
          border: 2px solid #114232;
          border-radius: 16px;
          padding: 32px;
          text-align: center;
        }

        .success-icon {
          font-size: 48px;
          margin-bottom: 16px;
        }

        .success-title {
          font-size: 20px;
          font-weight: 700;
          color: #114232;
          margin-bottom: 12px;
        }

        .success-message {
          font-size: 14px;
          color: #999;
          line-height: 1.6;
          margin-bottom: 24px;
        }

        .back-button {
          display: inline-block;
          background-color: #114232;
          border: none;
          border-radius: 12px;
          padding: 12px 24px;
          color: #fff;
          font-size: 14px;
          font-weight: 700;
          text-decoration: none;
          cursor: pointer;
        }

        .back-button:hover {
          background-color: #1a6b4f;
        }
      `}</style>
    </div>
  );
}
